/**
 * Pure functions extracted from views/card/index.tsx and
 * components/ScorecardLoadingView.tsx for testability.
 */

import type { IssueGroup, PlaceBasic, PlacePeriodReport, PlaceSourceSnapshot, Task } from './types';
import { GOOGLE_PIPELINE_TASKS } from './types';

/**
 * Sort tasks by GOOGLE_PIPELINE_TASKS order, then progress ascending,
 * then created_at ascending.
 */
export function sortTasks(a: Task, b: Task): number {
  const aIndex = GOOGLE_PIPELINE_TASKS.indexOf(a.name);
  const bIndex = GOOGLE_PIPELINE_TASKS.indexOf(b.name);
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  // Same index: sort by progress ascending (lower progress = more urgent)
  const aProgress = a.progress ?? 0;
  const bProgress = b.progress ?? 0;
  if (aProgress !== bProgress) {
    return aProgress - bProgress;
  }
  // Same progress: sort by created_at ascending
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

/**
 * Check if Google periods exist in the report's periods array.
 * Periods with no source are treated as google (backwards compatibility).
 */
export function computeHasGooglePeriods(periods: PlacePeriodReport[]): boolean {
  return periods.some((period) => periodMatchesSource(period, 'google'));
}

export function periodMatchesSource(
  period: Pick<PlacePeriodReport, 'source'>,
  source: string,
): boolean {
  return source === 'google'
    ? !period.source || period.source === 'google'
    : period.source === source;
}

export type SourceAvailabilityState = 'data' | 'loading' | 'unavailable';

export type SourceAvailability = {
  source: string;
  state: SourceAvailabilityState;
  hasData: boolean;
  isLoading: boolean;
  disabled: boolean;
  disabledReason?: string;
};

const REVIEW_SOURCE_TASK_NAMES = new Set([
  'collect_details_source',
  'collect_reviews',
  'collect_reviews_source',
  'flag_violations_source',
  'regenerate_report_source',
  'report_statistics',
  'report_statistics_source',
]);

function isLoadingTask(task: Task): boolean {
  if (task.status === 'completed') {
    return false;
  }
  if (task.status === 'failed' && task.retries != null && task.retries >= 3) {
    return false;
  }
  return true;
}

/**
 * Classify public scorecard source tabs without relying on UI state.
 *
 * Source-aware task hints prevent one slow collector from making every missing
 * tab look pending. Older API responses without source hints retain the broad
 * loading fallback. Period/issue data always wins over task state.
 */
export function computeSourceAvailabilityMap({
  visibleSources,
  periods,
  tasks,
  issueGroups,
}: {
  visibleSources: string[];
  periods: PlacePeriodReport[];
  tasks: Task[];
  issueGroups?: IssueGroup[] | null;
}): Record<string, SourceAvailability> {
  const reviewSourceTasks = tasks.filter((task) => REVIEW_SOURCE_TASK_NAMES.has(task.name));
  const hasReviewSourceLoadingTask = reviewSourceTasks.some(
    (task) => REVIEW_SOURCE_TASK_NAMES.has(task.name) && isLoadingTask(task),
  );
  const hasUnscopedReviewSourceLoadingTask = reviewSourceTasks.some(
    (task) => !task.source && isLoadingTask(task),
  );
  const hasAiLoadingTask = tasks.some(
    (task) => task.name === 'extract_report_issues' && isLoadingTask(task),
  );

  const availability: Record<string, SourceAvailability> = {};

  for (const source of visibleSources) {
    const hasData =
      source === 'ai'
        ? !!issueGroups && issueGroups.length > 0
        : periods.some((period) => periodMatchesSource(period, source));
    const isLoading =
      source === 'ai'
        ? hasAiLoadingTask || hasReviewSourceLoadingTask
        : reviewSourceTasks.some((task) => task.source === source && isLoadingTask(task))
          || hasUnscopedReviewSourceLoadingTask;
    const state: SourceAvailabilityState = hasData
      ? 'data'
      : isLoading
        ? 'loading'
        : 'unavailable';
    const disabled = state !== 'data';

    availability[source] = {
      source,
      state,
      hasData,
      isLoading,
      disabled,
      disabledReason:
        state === 'loading'
          ? source === 'ai'
            ? 'Loading AI analysis'
            : 'Loading review data'
          : state === 'unavailable'
            ? 'No data available'
            : undefined,
    };
  }

  return availability;
}

/**
 * Compute the overall loading status of a scorecard.
 *
 * - 'success' when Google periods exist (primary source ready)
 * - 'error' when ALL tasks are terminal (completed or failed ≥3 retries) and no Google periods
 * - 'in-progress' otherwise
 */
export function computeStatus(
  tasks: Task[],
  hasGooglePeriods: boolean,
): 'success' | 'error' | 'in-progress' {
  if (hasGooglePeriods) {
    return 'success';
  }

  const allTasksTerminal =
    tasks.length > 0 &&
    tasks.every(
      (t) =>
        t.status === 'completed' ||
        (t.status === 'failed' && t.retries != null && t.retries >= 3),
    );

  if (allTasksTerminal) {
    return 'error';
  }

  return 'in-progress';
}

/**
 * Find the current (most relevant) active task from the Google pipeline.
 * Returns undefined when there are no tasks or all pipeline tasks are completed.
 */
export function computeCurrentTask(tasks: Task[]): Task | undefined {
  if (tasks.length === 0) {
    return undefined;
  }
  const filteredTasks = [...tasks]
    .filter((task) => GOOGLE_PIPELINE_TASKS.includes(task.name) && task.status !== 'completed')
    .sort(sortTasks);
  return filteredTasks[0];
}

/**
 * Calculate scorecard creation progress (0–100) based on the current task.
 *
 * - Returns 0 when there is no current task.
 * - Uses real task.progress (scaled to 30–90 range) when available.
 * - Falls back to a static estimate per task name.
 */
export function computeProgress(currentTask: Task | undefined): number {
  if (!currentTask) {
    return 0;
  }

  // If task has real progress, scale it to the appropriate range
  if (currentTask.progress != null && currentTask.progress > 0) {
    if (currentTask.name === 'collect_reviews_source') {
      // Scale to 30-70 range
      return 30 + (currentTask.progress / 100) * 40;
    }
    if (currentTask.name === 'flag_violations_source') {
      // Scale to 70-90 range
      return 70 + (currentTask.progress / 100) * 20;
    }
    // Generic: scale to 30-90 range
    return 30 + (currentTask.progress / 100) * 60;
  }

  // Estimate based on which task we're on
  const taskProgressMap: Record<string, number> = {
    collect_details: 5,
    collect_details_source: 10,
    collect_maps: 15,
    collect_reviews: 20,
    collect_reviews_source: 30,
    flag_violations_source: 70,
  };

  return taskProgressMap[currentTask.name] ?? 25;
}

/**
 * Determine whether source rating cards should be shown.
 * True when the place has any non-null rating from any source.
 */
export function computeShowSourceRatings(place: PlaceBasic | null | undefined): boolean {
  if (!place) return false;
  return (
    place.rating != null ||
    place.yelp_rating != null ||
    place.bbb_rating != null ||
    place.dealerrater_rating != null ||
    place.carscom_rating != null ||
    place.carfax_rating != null ||
    place.edmunds_rating != null ||
    place.cargurus_rating != null ||
    place.opentable_rating != null ||
    place.tripadvisor_rating != null ||
    place.booking_rating != null ||
    place.expedia_rating != null ||
    place.golfpass_rating != null
  );
}

export type SourceVisibility = {
  isAutomotive: boolean;
  hasDealerRater: boolean;
  hasCarsCom: boolean;
  hasCarFax: boolean;
  hasEdmunds: boolean;
  hasCarGurus: boolean;
  isRestaurant: boolean;
  hasOpenTable: boolean;
  isHotel: boolean;
  isGolf: boolean;
  hasTripAdvisor: boolean;
  hasBooking: boolean;
  hasExpedia: boolean;
  hasGolfPass: boolean;
};

/**
 * Compute which review sources should be visible based on business type,
 * place links, and available source snapshots.
 */
export function computeSourceVisibility(
  businessType: string | null | undefined,
  place: PlaceBasic | null | undefined,
  sourceSnapshots: PlaceSourceSnapshot[],
): SourceVisibility {
  const snapshotMap = new Map<string, PlaceSourceSnapshot>();
  for (const s of sourceSnapshots) {
    snapshotMap.set(s.source, s);
  }
  const hasSourceSnapshot = (source: string) => !!snapshotMap.get(source)?.link;

  const isAutomotive = businessType === 'Automotive';
  const hasDealerRater = hasSourceSnapshot('dealerrater') || !!place?.dealerrater_link || isAutomotive;
  const hasCarsCom = hasSourceSnapshot('carscom') || !!place?.carscom_link || isAutomotive;
  const hasCarFax = hasSourceSnapshot('carfax') || !!place?.carfax_link || isAutomotive;
  const hasEdmunds = hasSourceSnapshot('edmunds') || !!place?.edmunds_link || isAutomotive;
  const hasCarGurus = hasSourceSnapshot('cargurus') || !!place?.cargurus_link || isAutomotive;

  const isRestaurant = businessType === 'Restaurant';
  const hasOpenTable = hasSourceSnapshot('opentable') || !!place?.opentable_link || isRestaurant;

  const isHotel = businessType === 'Hotel';
  const isGolf = businessType === 'Golf';
  const hasTripAdvisor =
    hasSourceSnapshot('tripadvisor') ||
    !!place?.tripadvisor_link ||
    isHotel ||
    isRestaurant ||
    isGolf;

  const hasBooking = hasSourceSnapshot('booking') || !!place?.booking_link || isHotel;
  const hasExpedia = hasSourceSnapshot('expedia') || !!place?.expedia_link || isHotel;
  const hasGolfPass = hasSourceSnapshot('golfpass') || !!place?.golfpass_link || isGolf;

  return {
    isAutomotive,
    hasDealerRater,
    hasCarsCom,
    hasCarFax,
    hasEdmunds,
    hasCarGurus,
    isRestaurant,
    hasOpenTable,
    isHotel,
    isGolf,
    hasTripAdvisor,
    hasBooking,
    hasExpedia,
    hasGolfPass,
  };
}
